import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import AppShell from '../components/AppShell';
import {
  Copy, Check, ExternalLink, Users, DollarSign, TrendingUp,
  Clock, ArrowRight, ShieldCheck, AlertTriangle, Calculator,
  CreditCard, Calendar, Zap, ChevronRight, Link2, BarChart3,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/* ═══════════════════════════════════════════════════
   METRIC CARD
   ═══════════════════════════════════════════════════ */
function MetricCard({ icon: Icon, label, value, sub, color = 'zinc', testId }) {
  const colors = {
    orange: 'bg-orange-500/10 text-orange-400',
    emerald: 'bg-emerald-500/10 text-emerald-400',
    blue: 'bg-blue-500/10 text-blue-400',
    amber: 'bg-amber-500/10 text-amber-400',
    zinc: 'bg-zinc-800/50 text-zinc-400',
    red: 'bg-red-500/10 text-red-400',
  };
  return (
    <div className="bg-zinc-900/50 border border-zinc-800/40 rounded-xl px-3.5 py-3" data-testid={testId}>
      <div className="flex items-center gap-2 mb-1.5">
        <div className={`w-6 h-6 rounded-md flex items-center justify-center ${colors[color]}`}>
          <Icon className="w-3 h-3" />
        </div>
        <span className="text-[10px] text-zinc-500 uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-lg font-bold text-zinc-100 tabular-nums">{value}</p>
      {sub && <p className="text-[10px] text-zinc-600 mt-0.5">{sub}</p>}
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   COMPLIANCE MODAL (for existing affiliates)
   ═══════════════════════════════════════════════════ */
function ComplianceModal({ onAccept, loading }) {
  const [agreed, setAgreed] = useState(false);

  const rules = [
    'No spam or unsolicited bulk messaging',
    'No misleading earnings or income claims',
    'No impersonation of Delphi AI or its team',
    'No brand bidding on paid ads (e.g., "Delphi AI" keywords)',
    'No fake demos, altered screenshots, or fabricated testimonials',
    'Always disclose affiliate relationships (FTC requirement)',
    'Focus on honest product recommendations',
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="compliance-modal">
      <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-5 max-w-md w-full mx-4 shadow-2xl">
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck className="w-5 h-5 text-orange-400" />
          <h2 className="text-sm font-semibold text-zinc-100">Affiliate Promotion Policy</h2>
        </div>
        <p className="text-xs text-zinc-400 mb-3">To continue using affiliate features, please review and accept our promotion policies:</p>
        <div className="bg-zinc-950/50 rounded-lg p-3 mb-4 max-h-48 overflow-y-auto">
          <ul className="space-y-1.5">
            {rules.map((r, i) => (
              <li key={i} className="flex items-start gap-2 text-[11px] text-zinc-400">
                <span className="text-orange-400 shrink-0 mt-0.5">•</span>
                {r}
              </li>
            ))}
          </ul>
        </div>
        <label className="flex items-start gap-2 mb-4 cursor-pointer" data-testid="compliance-checkbox-label">
          <input
            type="checkbox"
            checked={agreed}
            onChange={e => setAgreed(e.target.checked)}
            className="mt-0.5 accent-orange-500"
            data-testid="compliance-checkbox"
          />
          <span className="text-[11px] text-zinc-300">I agree to comply with Delphi AI's affiliate promotion policies.</span>
        </label>
        <button
          onClick={onAccept}
          disabled={!agreed || loading}
          className="w-full py-2 text-xs font-medium rounded-lg oracle-flame-bg text-white disabled:opacity-40 transition-opacity"
          data-testid="compliance-accept-btn"
        >
          {loading ? 'Accepting...' : 'Accept & Continue'}
        </button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   EARNINGS ESTIMATOR
   ═══════════════════════════════════════════════════ */
function EarningsEstimator({ commissionRate }) {
  const [referrals, setReferrals] = useState(5);
  const creatorPlusPrice = 129; // $129/mo
  const monthlyPerRef = creatorPlusPrice * commissionRate;
  const monthly = referrals * monthlyPerRef;
  const annual = monthly * 12;
  const lifetime = monthly * 12; // 12-month retention baseline

  return (
    <div className="bg-zinc-900/50 border border-zinc-800/40 rounded-xl p-4" data-testid="earnings-estimator">
      <div className="flex items-center gap-2 mb-3">
        <Calculator className="w-4 h-4 text-orange-400" />
        <h3 className="text-xs font-semibold text-zinc-200 uppercase tracking-wider">Earnings Estimator</h3>
      </div>
      <div className="mb-3">
        <label className="text-[10px] text-zinc-500 mb-1 block">Creator+ referrals per month</label>
        <input
          type="number"
          min={1}
          max={100}
          value={referrals}
          onChange={e => setReferrals(Math.max(1, Math.min(100, parseInt(e.target.value) || 1)))}
          className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-zinc-100 focus:border-orange-500/50 focus:outline-none"
          data-testid="estimator-input"
        />
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div className="bg-zinc-950/50 rounded-lg px-3 py-2 text-center">
          <p className="text-[9px] text-zinc-600 uppercase">Monthly</p>
          <p className="text-sm font-bold text-emerald-400 tabular-nums" data-testid="est-monthly">${monthly.toFixed(2)}</p>
        </div>
        <div className="bg-zinc-950/50 rounded-lg px-3 py-2 text-center">
          <p className="text-[9px] text-zinc-600 uppercase">Annual</p>
          <p className="text-sm font-bold text-emerald-400 tabular-nums" data-testid="est-annual">${annual.toFixed(2)}</p>
        </div>
        <div className="bg-zinc-950/50 rounded-lg px-3 py-2 text-center">
          <p className="text-[9px] text-zinc-600 uppercase">Lifetime (12mo)</p>
          <p className="text-sm font-bold text-emerald-400 tabular-nums" data-testid="est-lifetime">${lifetime.toFixed(2)}</p>
        </div>
      </div>
      <p className="text-[10px] text-zinc-600 mt-2 text-center">
        Based on Creator+ at $129/mo × {(commissionRate * 100).toFixed(0)}% commission × 12-month retention
      </p>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   PAYOUT POLICY PANEL
   ═══════════════════════════════════════════════════ */
function PayoutPolicy({ data }) {
  const items = [
    { label: 'Commission Rate', value: data.commission_rate || '25%', icon: TrendingUp },
    { label: 'Hold Period', value: `${data.hold_days || 14} days`, icon: Clock },
    { label: 'Minimum Payout', value: `$${data.payout_threshold_usd || 50}`, icon: DollarSign },
    { label: 'Payout Schedule', value: `${data.next_payout_day || 1}st of each month, 02:00 UTC`, icon: Calendar },
    { label: 'Payout Method', value: 'Stripe Connect Express', icon: CreditCard },
  ];

  return (
    <div className="bg-zinc-900/50 border border-zinc-800/40 rounded-xl p-4" data-testid="payout-policy">
      <div className="flex items-center gap-2 mb-3">
        <DollarSign className="w-4 h-4 text-orange-400" />
        <h3 className="text-xs font-semibold text-zinc-200 uppercase tracking-wider">Payout Policy</h3>
      </div>
      <div className="space-y-2">
        {items.map((item, i) => (
          <div key={i} className="flex items-center gap-2.5 text-[11px]">
            <item.icon className="w-3.5 h-3.5 text-zinc-600 shrink-0" />
            <span className="text-zinc-500">{item.label}:</span>
            <span className="text-zinc-200 font-medium">{item.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   JOIN LANDING SECTION
   ═══════════════════════════════════════════════════ */
function JoinSection({ onJoin, loading }) {
  const [agreed, setAgreed] = useState(false);

  return (
    <div className="max-w-lg mx-auto text-center" data-testid="affiliate-join-section">
      <div className="w-12 h-12 rounded-2xl oracle-flame-bg flex items-center justify-center mx-auto mb-4">
        <TrendingUp className="w-6 h-6 text-white" />
      </div>
      <h1 className="text-xl sm:text-2xl font-bold text-zinc-100 mb-2">Earn 25% Recurring Promoting Delphi AI</h1>
      <p className="text-xs text-zinc-400 mb-6 max-w-sm mx-auto">Join our affiliate program and earn commission on every referred subscription — for the lifetime of the customer.</p>

      <div className="bg-zinc-900/50 border border-zinc-800/40 rounded-xl p-4 mb-5 text-left">
        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <DollarSign className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-xs text-zinc-200 font-medium">25% recurring commission</p>
              <p className="text-[10px] text-zinc-500">Earn on every payment, every month, for the customer's lifetime.</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Clock className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-xs text-zinc-200 font-medium">12-month attribution window</p>
              <p className="text-[10px] text-zinc-500">Your referral link stays active for a full year after first click.</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Zap className="w-4 h-4 text-orange-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-xs text-zinc-200 font-medium">Ready-to-use marketing kit</p>
              <p className="text-[10px] text-zinc-500">30 image assets, 55+ copy templates, video scripts, email swipes.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Earnings example */}
      <div className="bg-zinc-900/50 border border-zinc-800/40 rounded-xl p-4 mb-5 text-left">
        <p className="text-[10px] text-zinc-600 uppercase tracking-wider mb-2">Example Earnings</p>
        <div className="flex items-center justify-between text-xs">
          <span className="text-zinc-400">5 Creator+ referrals/mo ($129 each)</span>
          <span className="text-emerald-400 font-bold">$161.25/mo</span>
        </div>
        <div className="flex items-center justify-between text-xs mt-1">
          <span className="text-zinc-400">Annual (12-month retention)</span>
          <span className="text-emerald-400 font-bold">$1,935.00/yr</span>
        </div>
      </div>

      {/* Compliance checkbox */}
      <label className="flex items-start gap-2 mb-4 text-left cursor-pointer px-2" data-testid="join-compliance-label">
        <input
          type="checkbox"
          checked={agreed}
          onChange={e => setAgreed(e.target.checked)}
          className="mt-0.5 accent-orange-500"
          data-testid="join-compliance-checkbox"
        />
        <span className="text-[11px] text-zinc-400">I agree to comply with Delphi AI's <span className="text-orange-400">affiliate promotion policies</span> (no spam, no misleading claims, always disclose).</span>
      </label>

      <button
        onClick={onJoin}
        disabled={!agreed || loading}
        className="w-full py-2.5 text-sm font-medium rounded-xl oracle-flame-bg text-white disabled:opacity-40 transition-opacity mb-3"
        data-testid="become-affiliate-btn"
      >
        {loading ? 'Joining...' : 'Become an Affiliate'}
      </button>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════════════════ */
function AffiliatesPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [showComplianceModal, setShowComplianceModal] = useState(false);
  const [acceptingCompliance, setAcceptingCompliance] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/affiliates/status`, { headers });
      const d = await r.json();
      setData(d);

      // Check if existing affiliate needs compliance acceptance
      if (d.status !== 'not_joined' && !d.compliance_accepted_at) {
        setShowComplianceModal(true);
      }
    } catch {}
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const handleJoin = async () => {
    setJoining(true);
    try {
      const r = await fetch(`${API}/api/affiliates/join`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ compliance_agreed: true }),
      });
      if (r.ok) {
        toast.success('Welcome to the affiliate program!');
        load();
      } else {
        const e = await r.json();
        toast.error(e.detail || 'Failed to join');
      }
    } catch { toast.error('Failed to join'); }
    setJoining(false);
  };

  const handleAcceptCompliance = async () => {
    setAcceptingCompliance(true);
    try {
      const r = await fetch(`${API}/api/affiliates/accept-compliance`, { method: 'POST', headers });
      if (r.ok) {
        setShowComplianceModal(false);
        toast.success('Policy accepted');
        load();
      }
    } catch {}
    setAcceptingCompliance(false);
  };

  const copyLink = () => {
    if (!data?.code) return;
    const link = `https://app.delphiai.ca/?ref=${data.code}`;
    navigator.clipboard.writeText(link);
    setLinkCopied(true);
    toast.success('Affiliate link copied!');
    setTimeout(() => setLinkCopied(false), 2000);
  };

  if (loading) return (
    <AppShell>
      <div className="flex items-center justify-center py-20">
        <div className="w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    </AppShell>
  );

  // Not yet an affiliate — show join page
  if (!data || data.status === 'not_joined') {
    return (
      <AppShell>
        <div className="p-5 sm:p-7 lg:p-10">
          <div className="mb-10">
            <p className="text-[10px] text-primary/60 uppercase tracking-[0.2em] font-semibold mb-3">Growth Engine</p>
            <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">Partner Performance</h1>
          </div>
          <JoinSection onJoin={handleJoin} loading={joining} />
        </div>
      </AppShell>
    );
  }

  // Affiliate dashboard
  const link = `https://app.delphiai.ca/?ref=${data.code}`;
  const b = data.balances || {};
  const m = data.metrics || {};

  return (
    <AppShell>
      <div className="p-5 sm:p-7 lg:p-10" data-testid="affiliate-dashboard">
        <div className="mb-10">
          <p className="text-[10px] text-primary/60 uppercase tracking-[0.2em] font-semibold mb-3">Growth Engine</p>
          <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">Partner Performance Overview</h1>
        </div>
        <div className="space-y-5">
      {/* Compliance gate modal */}
      {showComplianceModal && (
        <ComplianceModal onAccept={handleAcceptCompliance} loading={acceptingCompliance} />
      )}

      {/* Link bar */}
      <div className="bg-zinc-900/50 border border-zinc-800/40 rounded-xl px-4 py-3 flex flex-col sm:flex-row items-start sm:items-center gap-3" data-testid="aff-link-bar">
        <div className="flex-1 min-w-0">
          <p className="text-[10px] text-zinc-500 mb-0.5">Your affiliate link</p>
          <p className="text-xs text-orange-400 font-mono truncate" data-testid="aff-link">{link}</p>
        </div>
        <div className="flex gap-2 shrink-0">
          <button onClick={copyLink} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-orange-600/20 text-orange-400 hover:bg-orange-600/30 transition-colors" data-testid="copy-aff-link">
            {linkCopied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            {linkCopied ? 'Copied!' : 'Copy Link'}
          </button>
          <button onClick={() => navigate('/affiliates/kit')} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-zinc-800 text-zinc-300 hover:bg-zinc-700 transition-colors" data-testid="view-kit-btn">
            <ExternalLink className="w-3.5 h-3.5" />
            Marketing Kit
          </button>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5" data-testid="metrics-grid">
        <MetricCard icon={Link2} label="Total Clicks" value={m.total_clicks || 0} color="zinc" testId="metric-clicks" />
        <MetricCard icon={Users} label="Total Signups" value={m.total_signups || 0} color="blue" testId="metric-signups" />
        <MetricCard icon={CreditCard} label="Paid Conversions" value={m.paid_conversions || 0} color="emerald" testId="metric-conversions" />
        <MetricCard icon={BarChart3} label="Conversion Rate" value={`${m.conversion_rate || 0}%`} color="amber" testId="metric-conv-rate" />
        <MetricCard icon={Clock} label="Pending" value={`$${b.pending_usd || '0.00'}`} sub={`${data.hold_days || 14}-day hold`} color="amber" testId="metric-pending" />
        <MetricCard icon={DollarSign} label="Available" value={`$${b.available_usd || '0.00'}`} color="emerald" testId="metric-available" />
        <MetricCard icon={TrendingUp} label="Lifetime Earned" value={`$${b.lifetime_earned_usd || '0.00'}`} color="orange" testId="metric-lifetime" />
        <MetricCard icon={Calendar} label="Next Payout" value={data.next_payout_date ? (() => { const d = Math.max(0, Math.ceil((new Date(data.next_payout_date) - new Date()) / 86400000)); return d > 0 ? `in ${d} days` : 'Today'; })() : '—'} sub={b.threshold_remaining_usd > 0 ? `$${b.threshold_remaining_usd} to reach $${data.payout_threshold_usd || 50} min` : 'Threshold met'} color="zinc" testId="metric-next-payout" />
      </div>

      {/* UGC Mode Highlight Card */}
      <div className="bg-gradient-to-r from-orange-950/30 to-zinc-900/50 border border-orange-500/20 rounded-xl px-4 py-3.5" data-testid="ugc-highlight-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-orange-500/15 flex items-center justify-center shrink-0">
              <Zap className="w-4.5 h-4.5 text-orange-400" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <p className="text-xs font-semibold text-zinc-100">New: UGC Mode (Pro+)</p>
                <span className="text-[8px] px-1.5 py-0.5 rounded bg-orange-500 text-white font-bold leading-none">HOT</span>
              </div>
              <p className="text-[10px] text-zinc-400 mt-0.5">AI-generated UGC ads convert 2-3x higher. Promote this feature to drive Pro upgrades and boost your commissions.</p>
            </div>
          </div>
          <div className="relative group shrink-0 ml-3">
            <button
              onClick={() => navigate('/affiliates/kit')}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-medium rounded-lg bg-orange-600/20 text-orange-400 hover:bg-orange-600/30 transition-colors whitespace-nowrap"
              data-testid="ugc-promote-link"
            >
              UGC Campaign Pack
              <ArrowRight className="w-3 h-3" />
            </button>
            <div className="absolute bottom-full right-0 mb-2 w-52 p-2.5 bg-zinc-800 border border-zinc-700 rounded-lg text-[10px] text-zinc-300 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
              UGC Mode is Delphi AI's premium ad creation feature. Affiliates promoting UGC see higher conversion because it solves a $300/video pain point.
            </div>
          </div>
        </div>
      </div>

      {/* Payout Confidence */}
      <div className="bg-emerald-950/15 border border-emerald-500/15 rounded-xl px-4 py-2.5 flex items-center gap-2.5" data-testid="payout-confidence">
        <DollarSign className="w-4 h-4 text-emerald-400 shrink-0" />
        <p className="text-[11px] text-zinc-300">Payouts are <span className="text-emerald-400 font-medium">automatic on the 1st of each month</span> — 14-day hold, $50 minimum. No action required from you.</p>
      </div>

      {/* Stripe Connect Status */}
      {data.connect_onboarding_status !== 'complete' && (
        <div className="bg-amber-950/20 border border-amber-500/20 rounded-xl px-4 py-3" data-testid="connect-cta">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-xs text-amber-300 font-medium">Complete Stripe Connect Setup</p>
              <p className="text-[10px] text-zinc-500 mt-0.5">You need to connect your bank account via Stripe to receive payouts.</p>
            </div>
            <ConnectButton token={token} status={data.connect_onboarding_status} />
          </div>
        </div>
      )}

      {/* Two-column: Estimator + Policy */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <EarningsEstimator commissionRate={data.commission_rate_decimal || 0.25} />
        <PayoutPolicy data={data} />
      </div>

      {/* Rules & Compliance */}
      <div className="bg-zinc-900/50 border border-zinc-800/40 rounded-xl p-4" data-testid="compliance-rules">
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck className="w-4 h-4 text-orange-400" />
          <h3 className="text-xs font-semibold text-zinc-200 uppercase tracking-wider">Protecting Your Earnings</h3>
        </div>
        <p className="text-[10px] text-zinc-500 mb-2.5">To keep your affiliate account in good standing and protect your commission payouts:</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
          {[
            'Keep promotions authentic — no spam or bulk messaging',
            'Share real experiences, not exaggerated earnings claims',
            'Represent Delphi AI honestly — no impersonation',
            'Avoid brand bidding on paid search ads',
            'Use real screenshots and demos only',
            'Always disclose your affiliate relationship',
            'Focus on genuine, helpful recommendations',
          ].map((rule, i) => (
            <div key={i} className="flex items-start gap-2 text-[11px] text-zinc-400">
              <span className="text-emerald-400/60 shrink-0">•</span>
              {rule}
            </div>
          ))}
        </div>
        {data.compliance_accepted_at && (
          <p className="text-[10px] text-zinc-600 mt-3">Policy accepted: {new Date(data.compliance_accepted_at).toLocaleDateString()}</p>
        )}
      </div>
      </div>
      </div>
    </AppShell>
  );
}

/* ═══════════════════════════════════════════════════
   STRIPE CONNECT BUTTON
   ═══════════════════════════════════════════════════ */
function ConnectButton({ token, status }) {
  const [loading, setLoading] = useState(false);

  const startConnect = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/affiliates/connect/start`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      if (d.url) {
        window.open(d.url, '_blank');
      } else {
        toast.error(d.detail || 'Failed to start onboarding');
      }
    } catch {
      toast.error('Failed to connect');
    }
    setLoading(false);
  };

  return (
    <button
      onClick={startConnect}
      disabled={loading}
      className="px-3 py-1.5 text-[10px] font-medium rounded-lg bg-amber-600/20 text-amber-400 hover:bg-amber-600/30 disabled:opacity-50 transition-colors shrink-0 flex items-center gap-1"
      data-testid="connect-stripe-btn"
    >
      {loading ? 'Loading...' : status === 'pending' ? 'Continue Setup' : 'Connect Stripe'}
      <ChevronRight className="w-3 h-3" />
    </button>
  );
}

export default AffiliatesPage;
